import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface DemoModeState {
  hasLegalInfo: boolean;
  hasActiveSubscription: boolean;
  acceptedOwnerDocuments: boolean;
}

export interface ApplicationState {
  countryCode: string;
  demoMode: DemoModeState;
  emailGetReceipt: boolean;
  takeoutDisabled: boolean;
  deliveryDisabled: boolean;
  orderPointDisabled: boolean;
  agreementsAccepted: boolean;
  catalogScrollPosition: number;
  catalogActiveCategoryId: string | undefined;
  catalogScrollToCategoryId: string | undefined;
  isSubscribedToMailings: boolean;
  wbIntegrationEnabled: boolean;
  previouslyAcceptedClientDocuments: boolean;
  deliveryAddressModal: { isOpen: boolean; isOrderAnotherPerson: boolean };
  addressViewModal: { isOpen: boolean; address: string; lat: number; lng: number; areManualCoordinates: boolean };
}

const initialState: ApplicationState = {
  countryCode: '',
  takeoutDisabled: true,
  deliveryDisabled: true,
  orderPointDisabled: true,
  emailGetReceipt: false,
  catalogScrollPosition: 0,
  catalogActiveCategoryId: undefined,
  catalogScrollToCategoryId: undefined,
  agreementsAccepted: false,
  isSubscribedToMailings: false,
  wbIntegrationEnabled: false,
  demoMode: {
    hasLegalInfo: true,
    hasActiveSubscription: true,
    acceptedOwnerDocuments: true,
  },
  previouslyAcceptedClientDocuments: false,
  deliveryAddressModal: { isOpen: false, isOrderAnotherPerson: false },
  addressViewModal: { isOpen: false, address: '', lat: 0, lng: 0, areManualCoordinates: false },
};

export const applicationSlice = createSlice({
  name: 'errors',
  initialState,

  reducers: {
    setTakeoutDisabled(state, { payload }: PayloadAction<boolean>) {
      state.takeoutDisabled = payload;
    },
    setDeliveryDisabled(state, { payload }: PayloadAction<boolean>) {
      state.deliveryDisabled = payload;
    },
    setOrderPointDisabled(state, { payload }: PayloadAction<boolean>) {
      state.orderPointDisabled = payload;
    },
    setCatalogScrollPosition(state, { payload }: PayloadAction<number>) {
      state.catalogScrollPosition = payload;
    },
    setCatalogActiveCategoryId(state, { payload }: PayloadAction<string | undefined>) {
      state.catalogActiveCategoryId = payload;
    },
    setCatalogScrollToCategoryId(state, { payload }: PayloadAction<string | undefined>) {
      state.catalogScrollToCategoryId = payload;
    },
    setCountryCode(state, { payload }: PayloadAction<string>) {
      state.countryCode = payload;
    },
    setDemoModeInfo(state, { payload }: PayloadAction<DemoModeState>) {
      state.demoMode = payload;
    },
    setAgreementsAccepted(state, { payload }: PayloadAction<boolean>) {
      state.agreementsAccepted = payload;
    },
    setPreviouslyAcceptedClientDocuments(state, { payload }: PayloadAction<boolean>) {
      state.previouslyAcceptedClientDocuments = payload;
    },
    setGetEmailReceipt(state, { payload }: PayloadAction<boolean>) {
      state.emailGetReceipt = payload;
    },
    setIsSubscribedToMailings(state, { payload }: PayloadAction<boolean>) {
      state.isSubscribedToMailings = payload;
    },
    setWbIntegrationEnabled(state, { payload }: PayloadAction<boolean>) {
      state.wbIntegrationEnabled = payload;
    },
    resetApplicationUserFlags(state) {
      state.agreementsAccepted = false;
      state.isSubscribedToMailings = false;
      state.previouslyAcceptedClientDocuments = false;
    },
    openDeliveryAddressModal(state, { payload }: PayloadAction<boolean>) {
      state.deliveryAddressModal = { isOpen: true, isOrderAnotherPerson: payload };
    },
    closeDeliveryAddressModal(state) {
      state.deliveryAddressModal = { isOpen: false, isOrderAnotherPerson: false };
    },
    openAddressViewModal(
      state,
      { payload }: PayloadAction<{ address: string; lat: number; lng: number; areManualCoordinates: boolean }>,
    ) {
      state.addressViewModal = { isOpen: true, ...payload };
    },
    closeAddressViewModal(state) {
      state.addressViewModal = { isOpen: false, address: '', lat: 0, lng: 0, areManualCoordinates: false };
    },
  },
});

export const {
  setCountryCode,
  setDemoModeInfo,
  setGetEmailReceipt,
  setTakeoutDisabled,
  setDeliveryDisabled,
  setOrderPointDisabled,
  setAgreementsAccepted,
  setCatalogScrollPosition,
  setCatalogActiveCategoryId,
  setCatalogScrollToCategoryId,
  setIsSubscribedToMailings,
  setWbIntegrationEnabled,
  setPreviouslyAcceptedClientDocuments,
  resetApplicationUserFlags,
  openDeliveryAddressModal,
  closeDeliveryAddressModal,
  openAddressViewModal,
  closeAddressViewModal,
} = applicationSlice.actions;

export const applicationReducer = applicationSlice.reducer;
